import { rememberLocaleChoice } from "./preferences.js";
import { kitConfig, signedOutRoute } from "./config.js";
import { call, forgetPublicIds, useApiQuery } from "./api.js";
import { hasLocaleChoice, isLocale, showLocale, useT } from "./i18n.js";
import { appLinks, appUrl } from "@hlt/contracts";
import { useIsMutating, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
// src/session/device-state.ts
const actorKey = "hlt.deviceActor";
/** Per-user conveniences kept in this browser; none of them may outlive the account on a shared device. */
const userScopedPrefixes = [
	"hlt.amendment.",
	"hlt.orgJob.",
	"hlt.analytics.lastPage.",
	"hlt.lastWorkspace",
	"hlt.sessionSnapshot"
];
const cleanups = [];
const pendingCounters = [];
/**
* Apps register what they keep on this device (for example the 5S tool's offline drafts and its offline
* copy of opened audits). Browser storage is per origin, so each app clears its own state when the account
* changes or signs out here. Mark cleanups that delete unsent work with `unsentWork`: a sign-out that
* started in another app removes everything else at once and asks before removing those. Returns a
* function that unregisters the cleanup.
*/
function registerDeviceCleanup(cleanup, options = {}) {
	const entry = {
		run: cleanup,
		unsentWork: options.unsentWork ?? false
	};
	cleanups.push(entry);
	return () => {
		const index = cleanups.indexOf(entry);
		if (index >= 0) cleanups.splice(index, 1);
	};
}
/** The account that last signed in on this device, known before any request (for example offline). */
function deviceActor() {
	try {
		return localStorage.getItem(actorKey);
	} catch {
		return null;
	}
}
/** Apps register a count of unsent work so sign-out can ask before discarding it. */
function registerPendingWork(count) {
	pendingCounters.push(count);
	return () => {
		const index = pendingCounters.indexOf(count);
		if (index >= 0) pendingCounters.splice(index, 1);
	};
}
async function pendingWorkCount() {
	return (await Promise.all(pendingCounters.map(async (count) => await count().catch(() => 0)))).reduce((sum, n) => sum + n, 0);
}
/**
* Remove the per-user conveniences of this origin (last workspace, page and job pointers, the session
* snapshot) but not drafts. Starting or ending an impersonation calls it, so nothing of the member stays
* behind for the operator, while the operator's own unsent drafts survive.
*/
function clearUserScopedState() {
	forgetPublicIds();
	try {
		for (const key of Object.keys(localStorage)) if (userScopedPrefixes.some((p) => key.startsWith(p))) localStorage.removeItem(key);
	} catch {}
}
/** Run every cleanup, even after one fails; rejects with all failures so callers can tell the person. */
async function runCleanups(selected) {
	const failures = [];
	for (const cleanup of selected) await cleanup.run().catch((error) => failures.push(error));
	if (failures.length) throw new AggregateError(failures, "Data kept on this device could not be removed");
}
/**
* Remove what this origin keeps of the account except unsent work: per-user state including the session
* snapshot, and copies of server data (the offline copy of opened audits). A sign-out that started in
* another app runs this before it asks about unsent drafts, so nothing of the account can be reopened
* offline while the question waits. The device stays claimed, so the drafts keep their owner.
*/
async function clearDeviceCopies() {
	clearUserScopedState();
	await runCleanups(cleanups.filter((c) => !c.unsentWork));
}
/**
* Remove local drafts and per-user browser state (sign-out, or another account signing in on this device).
* The device's account is forgotten last, and only when everything else was removed, so cleanups can still
* rely on it and a failed removal is retried on the next sign-out or account switch. Rejects when a cleanup
* failed (for example another tab still holds the drafts).
*/
async function clearDeviceState() {
	clearUserScopedState();
	await runCleanups(cleanups);
	try {
		localStorage.removeItem(actorKey);
	} catch {}
}
/**
* Called with the signed-in user on every protected navigation. When a different account than the last one
* signs in here, the previous account's drafts and preferences are removed before anything renders. Stored
* drafts are tagged with their account and never shown to another one, so a removal that fails (for example
* while another tab holds them) does not block the new account; it is reported and retried later.
*/
async function claimDevice(userId) {
	let previous = null;
	try {
		previous = localStorage.getItem(actorKey);
	} catch {
		return;
	}
	if (previous === userId) return;
	if (previous) await clearDeviceState().catch((error) => reportError(error));
	try {
		localStorage.setItem(actorKey, userId);
	} catch {}
}
// src/session/use-session.ts
/** Current identity (null when signed out). MFA-pending sessions report `mfaRequired`. */
function useSession() {
	return useApiQuery("getSession", {}, {
		retry: false,
		staleTime: 6e4
	});
}
/**
* Reload when the signed-in identity changes under an open page: one session cookie serves every tab and
* app, so an impersonation started or ended in another tab switches this one too. Nothing cached for the
* previous identity stays on screen. The session refetches on focus once it is a minute old.
*/
function useIdentityChangeReload() {
	const session = useSession();
	const client = useQueryClient();
	const seen = useRef(null);
	const identity = session.data ? `${session.data.userId}:${session.data.impersonation?.id ?? ""}` : null;
	useEffect(() => {
		if (!identity) return;
		if (seen.current && seen.current !== identity) {
			client.clear();
			clearUserScopedState();
			window.location.reload();
			return;
		}
		seen.current = identity;
	}, [identity, client]);
}
/**
* Sign out, clear every protected snapshot and the drafts and preferences kept on this device, let every
* other app clear its own (`signedOutRoute`), and end at sign-in. Unsent drafts are never removed
* silently: the person confirms here, or on another app's signed-out page.
*/
function useSignOut() {
	const client = useQueryClient();
	const t = useT();
	return useCallback(async () => {
		const unsent = await pendingWorkCount();
		if (unsent > 0 && !window.confirm(t("shell.signOutDrafts", { count: unsent }))) return;
		await call("signOut", { input: {} }).catch(() => void 0);
		client.clear();
		const { origins, serves } = kitConfig();
		const final = signedOutRoute(appUrl(origins, appLinks.signIn()));
		try {
			await clearDeviceState();
			window.location.assign(final);
		} catch {
			const own = serves[0];
			window.location.assign(own ? `${appUrl(origins, appLinks.signedOut(own))}?next=${encodeURIComponent(final)}` : final);
		}
	}, [client, t]);
}
// src/session/locale-sync.ts
/** Store `locale` as the account's language (emails and notifications use it). */
async function saveAccountLocale(locale) {
	const profile = await call("getProfile");
	if (profile.locale !== locale) await call("updateProfile", { input: {
		...profile,
		locale
	} });
}
/**
* True when the signed-in person's own account language may be read and stored. While HLT support uses the
* app as a member, the operator's own language stays and the member's account language is never changed.
*/
function useOwnsAccountLocale() {
	const session = useSession();
	const owns = Boolean(session.data && !session.data.mfaRequired && !session.data.impersonation);
	return {
		owns,
		account: owns ? session.data?.locale ?? null : null
	};
}
/**
* One language everywhere: a language picked in this browser wins and becomes the account's language too;
* without a browser choice, the account's language applies; with neither, the browser's own language stays
* and nothing is stored. Used once by every signed-in frame.
*/
function useAccountLocaleSync() {
	const { owns, account } = useOwnsAccountLocale();
	const client = useQueryClient();
	const { i18n } = useTranslation();
	const current = i18n.language;
	useEffect(() => {
		if (!owns || !isLocale(current)) return;
		if (hasLocaleChoice()) {
			if (current !== account) saveAccountLocale(current).then(() => client.invalidateQueries()).catch(() => void 0);
		} else if (isLocale(account) && account !== current) showLocale(i18n, account);
	}, [
		owns,
		account,
		current,
		client,
		i18n
	]);
}
const switchLocaleKey = ["switch-locale"];
/**
* Pick a language. The API answers customer content (template and audit titles) in the account's language,
* so when signed in the interface switches only after that language is stored and the queries on screen are
* fetched again: interface text and customer content change together. Signed out or while impersonating the
* interface switches at once, and a failed save still switches it.
*/
function useSwitchLocale() {
	const { owns } = useOwnsAccountLocale();
	const client = useQueryClient();
	const { i18n } = useTranslation();
	const { mutate, isPending, variables } = useMutation({
		mutationKey: switchLocaleKey,
		mutationFn: async (locale) => {
			if (owns) await saveAccountLocale(locale).then(() => client.invalidateQueries()).catch(() => void 0);
			rememberLocaleChoice(locale);
			showLocale(i18n, locale);
		}
	});
	return {
		switchLocale: useCallback((locale) => {
			if (locale !== i18n.language) mutate(locale);
		}, [i18n, mutate]),
		pending: isPending ? variables ?? null : null
	};
}
/** True while a language switch fetches content in the new language (any component may show progress). */
const useLocaleSwitching = () => useIsMutating({ mutationKey: switchLocaleKey }) > 0;
export { claimDevice, clearDeviceCopies, clearDeviceState, clearUserScopedState, deviceActor, pendingWorkCount, registerDeviceCleanup, registerPendingWork, useAccountLocaleSync, useIdentityChangeReload, useLocaleSwitching, useSession, useSignOut, useSwitchLocale };

//# sourceMappingURL=session.js.map